import React, { useEffect, useState } from "react";
import { useAuth } from "../services/auth/CognitoAuthContext.js";
import AuthLoginForm from "../components/AuthLoginForm";
import UserProfile from "../components/UserProfile";
import AuthLayout from "../components/AuthLayout.jsx";
import { useNavigate, useParams } from "react-router-dom";
import AuthRegisterUser from "../components/AuthRegisterUser.jsx";
import AuthUserConfirmation from "../components/AuthUserConfirmation";
import AuthForgotPassword from "../components/AuthForgotPassword.jsx";

/**
 * Example page demonstrating how to use the CognitoIdentityService with
 * the LoginForm and SignupForm components.
 */
export default function Auth() {
  let { action } = useParams();
  const [formValues, setFormValues] = useState({});
  const [message, setMessage] = useState(null);
  const { initialised, authService, session } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // registration is in progress, push them back
    if (localStorage.getItem("username")) {
      navigate("/auth/confirm");
    }
  }, [navigate, action]);

  const [redirectUri] = useState(() => {
    const redirectUri = localStorage.getItem("redirectUri");
    return redirectUri || "/";
  });

  // enforce valid auth route
  if (!["login", "logout", "register", "confirm", "forgot"].includes(action)) {
    navigate("/auth/login");
    return;
  }

  const handleChange = (key, value) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const handleSuccess = (action, result) => {
    // clear all other form values on success
    setFormValues({ username: formValues.username || "", email: formValues.email || "" });

    // display any message returned
    setMessage(result.message || null);

    const target = result.action;

    switch (target) {
      case "register":
        navigate("/auth/register");
        break;

      case "confirm":
        navigate("/auth/confirm");
        break;

      case "login":
        navigate("/auth/login");
        break;

      case "forgot":
        console.log("Not Implemented");
        break;

      case "authenticated":
        localStorage.removeItem("redirectUri");
        navigate(redirectUri);
        break;
    }
  };

  const handleError = (action, error) => {
    console.log(`${action} error:`, error);
  };

  const handleSignOut = async () => {
    if (initialised) {
      try {
        await authService.signOut();
        navigate("/");
        // The session listener will update the session state
      } catch (error) {
        console.error("Sign out error:", error);
      }
    }
  };

  const handleAccountSwitch = (existing) => {
    setFormValues({});
    if (existing) {
      navigate("/auth/login");
    } else {
      navigate("/auth/register");
    }
  };

  // Show loading state while initializing
  if (!initialised) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  // Show user info if authenticated
  if (session && session.authenticated) {
    return (
      <AuthLayout>
        <div className="mx-auto mt-8 max-w-md">
          <UserProfile userInfo={session.userInfo} onSignOut={handleSignOut} />
        </div>
      </AuthLayout>
    );
  }

  // Show login or signup form
  return (
    <AuthLayout>
      <div className="mx-auto mt-8 max-w-md">
        {message && <div className="text-proof-green text-center">{message}</div>}
        {action === "register" && (
          <>
            <AuthRegisterUser
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={(result) => handleSuccess("register", result)}
              onError={(error) => handleError("register", error)}
            />
            <div className="mt-4 text-center">
              <a onClick={() => handleAccountSwitch(true)} className="text-proof-blue hover:opacity-80">
                Already have an account? Sign in
              </a>
            </div>
          </>
        )}

        {action === "confirm" && (
          <>
            <AuthUserConfirmation
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={(result) => handleSuccess("confirm", result)}
              onError={(error) => handleError("confirm", error)}
            />
          </>
        )}

        {action === "forgot" && (
          <>
            <AuthForgotPassword
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={(result) => handleSuccess("confirm", result)}
              onError={(error) => handleError("confirm", error)}
            />
            <div className="mt-4 text-center">
              <a onClick={() => handleAccountSwitch(true)} className="text-proof-blue hover:opacity-80">
                Return to Sign in
              </a>
            </div>
          </>
        )}

        {action === "login" && (
          <>
            <AuthLoginForm
              state={formValues}
              onChange={handleChange}
              authService={authService}
              onSuccess={(result) => handleSuccess("login", result)}
              onError={(error) => handleError("login", error)}
            />
            <div className="mt-4 text-center">
              <a onClick={() => handleAccountSwitch(false)} className="text-proof-blue cursor-pointer hover:opacity-80">
                Don't have an account? Sign up
              </a>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
